import {
  ARENA_ACTION_SIZE,
  type ArenaAction,
  type ArenaCommand,
  type ArenaHistoryEntry,
  type ArenaView,
  type ArenaCard as Card,
  type Character,
  type Guild,
} from '@guildrise/shared';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { arenaRequest, fetchArenaHistory, fetchCollection } from '../../shared/api.js';
import { useTelegramBack } from '../../shared/telegram-back.js';
import { Button } from '../../shared/ui/controls.js';
import { GuildAvatar } from '../../shared/ui/guild-avatar.js';
import { Sheet } from '../../shared/ui/sheet.js';
import { ActionIcon } from './action-icon.js';
import { ArenaCard } from './arena-card.js';
import { ArenaChoice } from './arena-choice.js';
import { ArenaGuide, markGuideSeen, shouldShowGuide } from './arena-guide.js';
import { DeckEditor } from './deck-editor.js';
import { RankEmblem } from './rank-emblem.js';
import './arena.css';

const actions: ArenaAction[] = ['secret', 'discard', 'gift', 'split'];
const names: Record<ArenaAction, string> = {
  secret: 'Секрет',
  discard: 'Сброс',
  gift: 'Подарок',
  split: 'Разделение',
};
const hints: Record<ArenaAction, string> = {
  secret: 'Выбери карту: она раскроется в финале.',
  discard: 'Выбери карту, которую уберёшь из раунда.',
  gift: 'Выбери 3 карты. Соперник заберёт одну.',
  split: 'Выбери две пары: карты 1–2 и 3–4.',
};

export function ArenaScreen({
  catalog,
  guild,
  frameArt,
  onExit,
}: {
  readonly catalog: readonly Character[];
  readonly guild: Guild;
  readonly frameArt?: string | undefined;
  readonly onExit: () => void;
}) {
  const [match, setMatch] = useState<ArenaView | null>(null);
  const current = useRef<ArenaView | null>(null);
  const [owned, setOwned] = useState<string[]>([]);
  const [deck, setDeck] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(true);
  const [action, setAction] = useState<ArenaAction | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [zone, setZone] = useState<number | null>(null);
  const [detail, setDetail] = useState<Card | null>(null);
  const [history, setHistory] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [remaining, setRemaining] = useState(30);
  const [rankPreview, setRankPreview] = useState(false);
  const [guideOpen, setGuideOpen] = useState(() => shouldShowGuide(guild.id));
  const [matchHistory, setMatchHistory] = useState<ArenaHistoryEntry[]>([]);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyAttempt, setHistoryAttempt] = useState(0);
  function closeGuide() {
    markGuideSeen(guild.id);
    setGuideOpen(false);
  }
  useTelegramBack(() => {
    if (guideOpen) closeGuide();
    else if (detail) setDetail(null);
    else if (history) setHistory(false);
    else if (deckOpen) setDeckOpen(false);
    else if (action || (match?.pending?.yours && !offerDismissed)) {
      setAction(null);
      setSelected([]);
      setOfferDismissed(true);
    } else onExit();
  });
  const clock = useRef({ server: 0, received: 0 });
  const mounted = useRef(false);
  const locked = useRef(false);
  const intent = useRef<{ print: string; key: string } | null>(null);
  const commandAbort = useRef<AbortController | null>(null);
  const arenaElement = useRef<HTMLElement>(null);
  const cardPositions = useRef(new Map<string, DOMRect>());
  // biome-ignore lint/correctness/useExhaustiveDependencies: explicit retry reloads the same history
  useEffect(() => {
    if (!history) return;
    const controller = new AbortController();
    setHistoryLoading(true);
    setHistoryError('');
    fetchArenaHistory(controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setMatchHistory(items);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setHistoryError(e instanceof Error ? e.message : 'История недоступна');
      })
      .finally(() => {
        if (!controller.signal.aborted) setHistoryLoading(false);
      });
    return () => controller.abort();
  }, [history, historyAttempt]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: measure DOM positions only when the rendered match changes
  useLayoutEffect(() => {
    const root = arenaElement.current;
    if (!root) return;
    const previous = cardPositions.current;
    const next = new Map<string, DOMRect>();
    const animations: Animation[] = [];
    for (const element of root.querySelectorAll<HTMLElement>('[data-card-id]')) {
      const id = element.dataset.cardId;
      if (!id) continue;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      next.set(id, rect);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const from = previous.get(id);
        if (from && (Math.abs(from.x - rect.x) > 1 || Math.abs(from.y - rect.y) > 1)) {
          animations.push(
            element.animate(
              [
                {
                  transform: `translate(${from.x - rect.x}px,${from.y - rect.y}px) scale(${from.width / rect.width})`,
                  zIndex: 10,
                },
                { transform: 'none', zIndex: 10 },
              ],
              { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)' },
            ),
          );
        } else if (!from) {
          animations.push(
            element.animate(
              [
                { opacity: 0, transform: 'translateY(12px) rotateY(70deg)' },
                { opacity: 1, transform: 'none' },
              ],
              { duration: 320, easing: 'ease-out' },
            ),
          );
        }
      }
    }
    cardPositions.current = next;
    return () => {
      for (const animation of animations) animation.cancel();
    };
  }, [match?.revision, editing, action, offerDismissed]);
  const byId = new Map(catalog.map((c) => [c.character_id, c]));
  const valid =
    deck.length === 9 &&
    new Set(deck).size === 9 &&
    deck.every((id) => byId.has(id) && owned.includes(id));

  function accept(
    next: ArenaView | null,
    sentAt = performance.now(),
    receivedAt = performance.now(),
  ) {
    const previous = current.current;
    if (
      previous &&
      next &&
      ((previous.id === next.id && previous.revision > next.revision) ||
        (previous.serverNow ?? 0) > (next.serverNow ?? 0))
    )
      return;
    if (next?.serverNow)
      clock.current = { server: next.serverNow, received: (sentAt + receivedAt) / 2 };
    if (next?.id !== previous?.id || next?.revision !== previous?.revision) {
      setSelected([]);
      setAction(null);
      setOfferDismissed(false);
      setDetail(null);
    }
    current.current = next;
    setMatch(next);
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      commandAbort.current?.abort();
    };
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: explicit user retry and stable ref-based response acceptance
  useEffect(() => {
    const controller = new AbortController();
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(8000)]);
    setLoading(true);
    const sentAt = performance.now();
    Promise.all([
      arenaRequest('current', undefined, undefined, signal).then((next) => ({
        next,
        receivedAt: performance.now(),
      })),
      fetchCollection(signal),
    ])
      .then(([response, collection]) => {
        if (controller.signal.aborted) return;
        accept(response.next, sentAt, response.receivedAt);
        const ids = collection.characters.map((c) => c.characterId);
        setOwned(ids);
        setDeck((previous) => {
          if (previous.length) return previous.filter((id) => ids.includes(id));
          try {
            const saved: unknown = JSON.parse(
              localStorage.getItem(`guildrise:arena-deck:${guild.id}`) ?? 'null',
            );
            if (Array.isArray(saved))
              return [
                ...new Set(
                  saved.filter((id): id is string => typeof id === 'string' && ids.includes(id)),
                ),
              ].slice(0, 9);
          } catch {}
          return ids.slice(0, 9);
        });
        setError('');
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : 'Не удалось загрузить Арену');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [attempt]);
  const matchId = match?.id;
  const finished = match?.finished;
  // biome-ignore lint/correctness/useExhaustiveDependencies: accept reads current state through refs; one serialized polling loop per match
  useEffect(() => {
    if (!matchId || finished) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        if (!locked.current) {
          const sentAt = performance.now();
          const next = await arenaRequest('current', undefined, undefined, controller.signal);
          if (!controller.signal.aborted && !locked.current) {
            accept(next, sentAt);
            setError('');
          }
        }
      } catch {
        if (!controller.signal.aborted) setError('Восстанавливаем связь… Таймер продолжает идти.');
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void poll(), 1500);
      }
    };
    timer = setTimeout(() => void poll(), 500);
    const tick = setInterval(() => {
      const deadline = current.current?.deadlineAt;
      const now = clock.current.server + performance.now() - clock.current.received;
      setRemaining(deadline ? Math.max(0, Math.ceil((deadline - now) / 1000)) : 30);
    }, 200);
    return () => {
      controller.abort();
      clearTimeout(timer);
      clearInterval(tick);
    };
  }, [matchId, finished, editing]);

  async function send(command?: ArenaCommand) {
    if (locked.current) return;
    locked.current = true;
    commandAbort.current = new AbortController();
    setBusy(true);
    setError('');
    const previousMatchId = match?.id;
    const body =
      command && match ? { matchId: match.id, revision: match.revision, command } : { cards: deck };
    const print = JSON.stringify(body);
    if (intent.current?.print !== print) intent.current = { print, key: crypto.randomUUID() };
    try {
      const sentAt = performance.now();
      const next = await arenaRequest(
        command ? 'move' : 'start',
        body,
        intent.current.key,
        commandAbort.current.signal,
      );
      if (!mounted.current) return;
      accept(next, sentAt);
      setGuideOpen(false);
      setDeckOpen(false);
      setHistory(false);
      setEditing(false);
      setSelected([]);
      intent.current = null;
    } catch (e) {
      if (mounted.current) {
        setSelected([]);
        setError(e instanceof Error ? e.message : 'Связь прервалась. Восстанавливаем матч…');
        locked.current = false;
        setBusy(false);
        const sentAt = performance.now();
        void arenaRequest('current', undefined, undefined, commandAbort.current?.signal)
          .then((next) => {
            if (
              mounted.current &&
              next &&
              (command || next.id !== previousMatchId || !next.finished)
            ) {
              accept(next, sentAt);
              setGuideOpen(false);
              setDeckOpen(false);
              setHistory(false);
              setEditing(false);
              setError('');
            }
          })
          .catch(() => {
            if (mounted.current) setError('Нет связи. Нажми «Повторить», чтобы восстановить матч.');
          });
      }
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const zoneOf = (card: Card) =>
    match?.zones.findIndex((z) => z.universes.includes(card.universe)) ?? 0;
  function renderCard(
    card: Card,
    onClick: () => void,
    extra: { selected?: boolean; secret?: boolean; order?: number; label?: string } = {},
  ) {
    return (
      <ArenaCard
        key={card.id}
        card={card}
        character={byId.get(card.characterId)}
        zone={zoneOf(card)}
        dimmed={zone !== null && zone !== zoneOf(card)}
        onClick={onClick}
        {...extra}
      />
    );
  }
  function board(side: ArenaView['opponent'], label: string, universes: string[]) {
    const reserved = side.board.find((c) => c.hidden || c.secret);
    const regular = side.board
      .filter((c) => c !== reserved || match?.finished)
      .filter((c) => !c.hidden && universes.includes(c.card.universe));
    return (
      <section className="arena-side" aria-label={label}>
        <div
          className="arena-board"
          style={
            {
              '--zone-cards': regular.length,
              '--zone-rows': Math.max(2, Math.ceil(regular.length / 2)),
            } as React.CSSProperties
          }
        >
          {regular.map((entry) => {
            return (
              <div key={entry.slot} className="arena-slot">
                {!entry.hidden &&
                  renderCard(entry.card, () => setDetail(entry.card), {
                    secret: entry.secret && !match?.finished,
                  })}
              </div>
            );
          })}
        </div>
      </section>
    );
  }
  function secretField(side: ArenaView['opponent'], own: boolean) {
    const secret = side.board.find((c) => c.hidden || c.secret);
    const concealed = secret && !match?.finished;
    return (
      <section
        className="arena-secret-field"
        aria-label={own ? 'Поле твоего Секрета' : 'Поле Секрета соперника'}
      >
        <span>Секрет</span>
        {concealed ? (
          <button
            type="button"
            className="arena-secret-card"
            aria-label={own ? 'Посмотреть свой Секрет' : 'Закрытая карта соперника'}
            disabled={!own || secret.hidden}
            onClick={() => {
              if (own && !secret.hidden) setDetail(secret.card);
            }}
          >
            <ActionIcon action="secret" />
          </button>
        ) : (
          <div
            className="arena-secret-empty"
            role="img"
            aria-label={
              match?.finished && secret ? 'Секрет раскрыт на поле' : 'Пустое место для Секрета'
            }
          >
            {match?.finished && secret ? '✓' : '◇'}
          </div>
        )}
      </section>
    );
  }
  const playing = Boolean(match && !editing);
  function changeDeck(ids: string[]) {
    setDeck(ids);
    try {
      localStorage.setItem(`guildrise:arena-deck:${guild.id}`, JSON.stringify(ids));
    } catch {}
  }
  const logos: Record<string, string> = {
    Bleach: 'bleach-trim.png',
    Naruto: 'naruto.svg',
    'One Piece': 'one-piece.png',
    'Hunter × Hunter': 'hunter.png',
  };
  return (
    <section ref={arenaElement} className={`arena${playing ? ' arena--match' : ''}`}>
      {!playing && (
        <header className="arena-heading">
          <button type="button" className="arena-back" onClick={onExit} aria-label="Назад в меню">
            ‹ Назад
          </button>
          <button
            type="button"
            className="arena-help"
            onClick={() => setGuideOpen(true)}
            aria-label="Правила Арены"
          >
            ?
          </button>
        </header>
      )}
      {error && (
        <div className="arena-error" role="alert">
          {error}
          {!playing && (
            <Button variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
              Повторить
            </Button>
          )}
        </div>
      )}
      {loading ? (
        <p role="status">Готовим Арену…</p>
      ) : !match || editing ? (
        <div className="arena-lobby">
          <div className="arena-rank">
            <RankEmblem />
            <div>
              <h2 className="h2">{rankPreview ? 'Пример ранга' : 'Без ранга'}</h2>
              <p className="hint">
                {rankPreview ? 'Примерка оформления' : 'Рейтинговые матчи ещё не открыты'}
              </p>
              <div className="arena-rank__bar">
                <span style={{ width: rankPreview ? '66.667%' : '0%' }} />
              </div>
              <strong className="tabular">{rankPreview ? '60 / 90' : '— / —'}</strong>
              <small>
                {rankPreview ? 'Пример очков до следующего ранга' : 'До следующего ранга'}
              </small>
            </div>
          </div>
          <button
            type="button"
            className="arena-rank-preview"
            onClick={() => setRankPreview((v) => !v)}
          >
            {rankPreview ? 'Закрыть примерку' : 'Примерка ранга'}
          </button>
          <div className="arena-lobby__nav">
            <Button variant="ghost" onClick={() => setDeckOpen(true)}>
              Моя колода
            </Button>
          </div>
          <Button variant="ghost" onClick={() => setHistory(true)}>
            История матчей
          </Button>
          <ArenaChoice
            open={deckOpen}
            title="Моя колода"
            presentation="page"
            onClose={() => setDeckOpen(false)}
          >
            <DeckEditor catalog={catalog} owned={owned} selected={deck} onChange={changeDeck} />
          </ArenaChoice>
          {owned.length < 9 && <p className="body">Для матча нужны 9 карт из Арсенала.</p>}
          <div className="arena-matchmaking">
            <Button
              wide
              disabled={busy || (!(match && !match.finished) && !valid)}
              onClick={() => {
                if (match && !match.finished) setEditing(false);
                else void send();
              }}
            >
              {busy ? 'Подготовка…' : 'Начать матч'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="arena-table">
            {!match.finished && (
              <fieldset className="arena-actions" aria-label="Жетоны действий">
                {actions.map((a) => (
                  <button
                    key={a}
                    type="button"
                    title={names[a]}
                    aria-label={names[a]}
                    disabled={
                      busy ||
                      match.self.used.includes(a) ||
                      remaining === 0 ||
                      Boolean(match.pending) ||
                      !match.yourTurn
                    }
                    aria-pressed={action === a}
                    onClick={() => {
                      setAction(a);
                      setSelected([]);
                    }}
                  >
                    <ActionIcon action={a} />
                    <span>{match.self.used.includes(a) ? '✓' : ARENA_ACTION_SIZE[a]}</span>
                  </button>
                ))}
              </fieldset>
            )}
            <div
              className={`arena-player arena-player--opponent${match.botThinking ? ' arena-player--active' : ''}`}
            >
              <GuildAvatar
                name={match.opponentGuild?.name ?? 'Затмение'}
                frameId={match.opponentGuild?.frameId ?? 'frame_silver'}
                logoUrl={match.opponentGuild?.logoUrl ?? undefined}
                size={32}
              />
              <strong className="arena-player__identity">
                {match.opponentGuild?.name ?? 'Затмение'}
                <small>{match.opponentGuild ? `#${match.opponentGuild.discriminator}` : ''}</small>
              </strong>
              <span>{match.botThinking ? 'Выбирает ход…' : ''}</span>
            </div>
            {secretField(match.opponent, false)}
            <section
              className="arena-lanes"
              aria-label="Игровые зоны"
              style={
                {
                  gridTemplateColumns: `repeat(${match.zones.length}, minmax(0, 1fr))`,
                  '--lane-height':
                    'clamp(32px, calc((100dvh - var(--app-safe-top) - var(--app-safe-bottom) - 412px) / 2), 190px)',
                } as React.CSSProperties
              }
            >
              {match.zones.map((z, i) => (
                <section
                  className={`arena-lane arena-zone-${i}`}
                  key={z.universes.join('|')}
                  aria-label={`Зона ${z.universes.join(' + ')}`}
                >
                  {board(
                    match.opponent,
                    `Карты соперника: ${z.universes.join(' + ')}`,
                    z.universes,
                  )}
                  <section className="arena-zones" aria-label="Зоны влияния">
                    {
                      <button
                        type="button"
                        key={z.universes.join('|')}
                        className={`arena-zone arena-zone-${i}${zone === i ? ' arena-zone--on' : ''}`}
                        aria-pressed={zone === i}
                        onClick={() => setZone((previous) => (previous === i ? null : i))}
                      >
                        <span className="arena-zone__logos">
                          {z.universes.map((universe) =>
                            logos[universe] ? (
                              <img
                                key={universe}
                                src={`/art/universes/${logos[universe]}`}
                                alt={universe}
                              />
                            ) : (
                              <strong key={universe}>{universe}</strong>
                            ),
                          )}
                        </span>
                        <span className="arena-zone__score tabular">
                          <span>{z.self}</span>
                          <small>:</small>
                          <span>{z.opponent}</span>
                        </span>
                        <span className="arena-zone__label">
                          {i + 1} ·{' '}
                          {z.self === z.opponent
                            ? 'Равенство'
                            : z.self > z.opponent
                              ? 'Твоя зона'
                              : 'Зона соперника'}
                        </span>
                      </button>
                    }
                  </section>
                  {board(match.self, `Твои карты: ${z.universes.join(' + ')}`, z.universes)}
                </section>
              ))}
            </section>
            {secretField(match.self, true)}
            <div
              className={`arena-player${match.yourTurn || match.pending?.yours ? ' arena-player--active' : ''}`}
            >
              <GuildAvatar
                name={guild.name}
                frameId={guild.frameId}
                frameArt={frameArt}
                logoUrl={guild.logoUrl ?? undefined}
                size={32}
              />
              <strong className="arena-player__identity">
                {guild.name}
                <small>#{guild.discriminator}</small>
              </strong>
              <span className="tabular">
                {!match.finished && !match.botThinking ? `${remaining} сек` : ''}
              </span>
            </div>
          </div>
          {match.finished ? (
            <ArenaChoice
              open={!editing && !detail}
              presentation="result"
              title={
                match.winner === 'self'
                  ? 'Победа'
                  : match.winner === 'opponent'
                    ? 'Поражение'
                    : 'Ничья'
              }
              onClose={onExit}
            >
              <div className={`arena-result arena-result--${match.winner}`}>
                <div className="arena-result__crest" aria-hidden="true">
                  <RankEmblem />
                  {match.winner === 'self' && (
                    <div className="arena-victory-sparks">
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <i key={i} style={{ '--spark': i } as React.CSSProperties} />
                      ))}
                    </div>
                  )}
                </div>
                <h2 className="h1">
                  {match.winner === 'self'
                    ? 'Победа'
                    : match.winner === 'opponent'
                      ? 'Поражение'
                      : 'Ничья'}
                </h2>
                <p className="body">
                  Секреты раскрыты · Твои зоны:{' '}
                  {match.zones.filter((z) => z.self > z.opponent).length}/{match.zones.length}
                </p>
                <Button onClick={() => setEditing(true)}>Следующий матч</Button>
                <Button variant="ghost" onClick={onExit}>
                  Меню
                </Button>
              </div>
            </ArenaChoice>
          ) : (
            <>
              {match.pending?.yours && offerDismissed && (
                <button
                  type="button"
                  className="arena-offer-reopen"
                  aria-label="Показать предложение соперника"
                  onClick={() => setOfferDismissed(false)}
                >
                  <ActionIcon action={match.pending.action} />
                </button>
              )}
              <ArenaChoice
                open={
                  Boolean(
                    (action && match.yourTurn) || (match.pending?.yours && !offerDismissed),
                  ) &&
                  !editing &&
                  !history &&
                  !guideOpen
                }
                title={
                  match.pending?.yours
                    ? match.pending.action === 'gift'
                      ? 'Выбери карту'
                      : 'Выбери пару'
                    : action
                      ? `${names[action]} · ${selected.length}/${ARENA_ACTION_SIZE[action]}`
                      : 'Выбор карт'
                }
                onClose={() => {
                  setAction(null);
                  setSelected([]);
                  setOfferDismissed(true);
                }}
              >
                <section className="arena-controls" aria-label="Выбор карт">
                  <span className="arena-choice__time tabular">{remaining} сек</span>
                  {error && <p role="alert">{error}</p>}
                  {match.pending?.yours ? (
                    <div className="arena-offer">
                      <p className="arena-instruction">
                        {match.pending.action === 'gift'
                          ? 'Забери одну карту. Две останутся сопернику.'
                          : 'Забери пару. Другая останется сопернику.'}
                      </p>
                      <div
                        className={`arena-offer__options${match.pending.action === 'split' ? ' arena-offer__options--split' : ''}`}
                      >
                        {match.pending.options.map((option, index) => (
                          <div
                            className="arena-offer__option"
                            key={option.map((c) => c.id).join('|')}
                          >
                            <div>
                              {option.map((c) =>
                                renderCard(
                                  c,
                                  () => {
                                    if (!busy && remaining > 0)
                                      void send({ type: 'choose', index });
                                  },
                                  {
                                    label: `Забрать ${option.length === 1 ? 'карту' : 'пару'}: ${option.map((card) => byId.get(card.characterId)?.name ?? card.characterId).join(' и ')}`,
                                  },
                                ),
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="arena-instruction">
                        {action ? hints[action] : 'Выбери любое неиспользованное действие.'}
                      </p>
                      <div
                        className={`arena-hand${action === 'split' ? ' arena-hand--split' : ''}`}
                      >
                        {match.self.hand.map((c) =>
                          renderCard(
                            c,
                            () => {
                              if (busy || !match.yourTurn || remaining === 0 || !action) return;
                              if (selected.includes(c.id)) {
                                setSelected(selected.filter((id) => id !== c.id));
                                return;
                              }
                              if (selected.length >= ARENA_ACTION_SIZE[action]) return;
                              const next = [...selected, c.id];
                              setSelected(next);
                              if (next.length === ARENA_ACTION_SIZE[action])
                                void send({ type: 'play', action, cards: next });
                            },
                            {
                              selected: selected.includes(c.id),
                              ...(selected.includes(c.id) ? { order: selected.indexOf(c.id) } : {}),
                            },
                          ),
                        )}
                      </div>
                    </>
                  )}
                </section>
              </ArenaChoice>
            </>
          )}
        </>
      )}
      <Sheet
        manageBack={false}
        open={detail !== null}
        title={detail ? (byId.get(detail.characterId)?.name ?? 'Карта') : 'Карта'}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <div className="arena-detail">
            <span className="arena-detail__portrait">
              <img
                className="arena-card__art"
                src={`/art/cards/${byId.get(detail.characterId)?.art ?? detail.characterId}-720.webp`}
                alt={byId.get(detail.characterId)?.name ?? ''}
              />
              <img
                className="arena-card__frame"
                src={`/art/rarity-frames/${byId.get(detail.characterId)?.rarity ?? 'rare'}.svg`}
                alt=""
              />
            </span>
            <p>
              {detail.universe} · {detail.influence} влияния · Зона {zoneOf(detail) + 1}
            </p>
          </div>
        )}
      </Sheet>
      <ArenaChoice
        presentation="page"
        open={history}
        title="История матчей"
        onClose={() => setHistory(false)}
      >
        {historyLoading ? (
          <p role="status">Загрузка…</p>
        ) : historyError ? (
          <div>
            <p role="alert">{historyError}</p>
            <Button variant="ghost" onClick={() => setHistoryAttempt((n) => n + 1)}>
              Повторить
            </Button>
          </div>
        ) : !matchHistory.length ? (
          <p className="hint">Первые матчи появятся здесь после завершения.</p>
        ) : (
          <ol className="arena-history-list">
            {matchHistory.map((item) => (
              <li key={item.id}>
                <strong>
                  {item.winner === 'self'
                    ? 'Победа'
                    : item.winner === 'opponent'
                      ? 'Поражение'
                      : 'Ничья'}
                </strong>
                <span>
                  {item.opponentGuild
                    ? `${item.opponentGuild.name} #${item.opponentGuild.discriminator}`
                    : 'Соперник'}{' '}
                  · {item.score.self}:{item.score.opponent}
                </span>
                <time dateTime={new Date(item.endedAt).toISOString()}>
                  {new Date(item.endedAt).toLocaleString('ru-RU')}
                </time>
              </li>
            ))}
          </ol>
        )}
      </ArenaChoice>
      <ArenaGuide open={guideOpen && editing && !loading} onClose={closeGuide} guildId={guild.id} />
    </section>
  );
}
